import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import OrderCell from 'app/frontend/base/components/OrderCell'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { useUserTeamPreferences } from 'app/frontend/base/config/queries'
import { userTeamVisibilities } from 'app/frontend/types/custom/UserTeamVisibility'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

const name = 'stored_team_preferences_attributes'

const TeamPreferencesTable = () => {
  const { t } = useTranslation()

  const { data } = useUserTeamPreferences()
  const { teams, guilds } = data ?? {
    teams: [] as wowaudit.Team[],
    guilds: [] as wowaudit.Guild[],
  }

  const { watch } = useFormContext()

  const preferences: wowaudit.UserTeamPreference[] = watch(name)

  const globallyConfiguredGuilds = preferences
    .filter((pref) => pref.team_id === null)
    .map((pref) => pref.owner_id)

  const columns: ColumnDef<wowaudit.UserTeamPreference>[] = useMemo(
    () => [
      {
        id: 'owner_id',
        header: 'Guild',
        cell: (info) => {
          const guild = guilds.find(
            (guild) => guild.id === info.row.original.owner_id,
          )

          if (!guild) {
            return (
              <div className='ml-2 text-muted-foreground'>
                {t('profile.team-preferences.unknown-guild')}
              </div>
            )
          }

          return (
            <div className='ml-2'>
              <Link to={guild.path}>{guild.name}</Link>
              <div className='text-xs text-muted-foreground'>
                {guild.realm.name} ({guild.realm.region})
              </div>
            </div>
          )
        },
      },
      {
        id: 'team_id',
        header: 'Team',
        cell: (info) => {
          return (
            <SelectField<wowaudit.TeamReference>
              layout='stacked'
              name={`${name}.${info.row.index}.team_id`}
              options={
                guilds.find((guild) => guild.id === info.row.original.owner_id)
                  ?.teamReferences ?? []
              }
              customPlaceholder={'All teams'}
              isClearable={true}
              optionLabelFn={(option) => option.name}
              customLabel={''}
              optionValueKey='id'
            />
          )
        },
      },
      {
        id: 'visibility',
        header: 'Visibility',
        size: 160,
        cell: (info) => {
          return (
            <SelectField
              layout='stacked'
              name={`${name}.${info.row.index}.visibility`}
              options={userTeamVisibilities}
              customLabel={''}
            />
          )
        },
      },
      {
        id: 'actions',
        header: 'Order',
        size: 90,
        cell: (info) => {
          return (
            <OrderCell
              name={name}
              index={info.row.index}
              totalLength={preferences.length}
            />
          )
        },
      },
    ],
    [guilds, teams, preferences.length, t],
  )

  return (
    <div>
      <DataTable
        className='mt-6'
        cellClassName='align-top px-2'
        includeHeader={preferences && preferences.length > 0}
        rowClassName={(pref) =>
          globallyConfiguredGuilds.includes(pref.owner_id) &&
          pref.team_id !== null
            ? 'opacity-30 pointer-events-none'
            : ''
        }
        columns={columns}
        data={preferences ?? []}
        emptyMessage={() =>
          t('profile.team-preferences.preferences-empty-message')
        }
      />
    </div>
  )
}

export default TeamPreferencesTable
